import { z } from 'zod';
import { safeGet, safeRemove, safeSet } from '../storage';

/**
 * PWA 홈 화면 추가(A2HS) 유도의 판별 로직 + 이벤트 캡처 스토어.
 *
 * 노출 조건은 세 갈래로 나뉜다:
 * - 이미 설치된 상태(standalone)면 영원히 노출하지 않는다.
 * - Android/Chrome 은 `beforeinstallprompt` 를 가로채 네이티브 프롬프트를 재생한다.
 * - iOS Safari 는 프로그래매틱 설치 API 가 없어 "공유 → 홈 화면에 추가" 수동 안내만 가능하다.
 *
 * `beforeinstallprompt` 는 문서당 1회만 발화하고 재발화하지 않는다. 로그인 화면에 머무는
 * 동안 발화하면 이후 마운트되는 컴포넌트는 영원히 이벤트를 받지 못하므로, 리스너를 React
 * 바깥 모듈 스코프에 두고(main.tsx 에서 부팅 시 등록) 컴포넌트는 스냅샷을 구독만 한다.
 */

/** Chrome 계열 전용 이벤트 — lib.dom 에 타입이 없어 로컬 선언. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const SNOOZE_KEY = 'axiom.install-prompt.snoozed-until';
export const SNOOZE_DAYS = 14;
const DAY_MS = 86_400_000;

/** 앱 부팅 직후 배너가 첫 화면과 경쟁하지 않도록 두는 지연. */
export const SHOW_DELAY_MS = 3_000;

// 인앱 브라우저(카카오톡·인스타그램 등)와 iOS 비 Safari 브라우저는 공유 시트의
// "홈 화면에 추가" 동선이 없거나 달라, 안내가 오히려 막다른 길이 된다.
const IN_APP_BROWSER = /KAKAOTALK|Instagram|FBAN|FBAV|FB_IAB|Line\/|NAVER\(inapp|DaumApps|Threads/i;
const NON_SAFARI_IOS = /CriOS|FxiOS|EdgiOS|OPiOS/i;

/* ── 이벤트 캡처 스토어 ─────────────────────────────────────────── */

let deferredEvent: BeforeInstallPromptEvent | null = null;
let installed = false;
let started = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** main.tsx 부팅 시 1회 호출. React 마운트보다 먼저 리스너를 걸어 이벤트 유실을 막는다. */
export function initInstallPromptCapture(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    // 브라우저 기본 미니 인포바를 막고 자체 배너 타이밍으로 통일
    e.preventDefault();
    deferredEvent = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferredEvent = null;
    clearSnooze();
    notify();
  });
}

export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return deferredEvent;
}

export function getInstalled(): boolean {
  return installed;
}

/** 네이티브 프롬프트는 1회용 — 재진입(더블탭)으로 두 번 호출되지 않게 동기적으로 비운다. */
export function consumeDeferredPrompt(): BeforeInstallPromptEvent | null {
  const event = deferredEvent;
  deferredEvent = null;
  notify();
  return event;
}

/** 테스트 전용: 모듈 스코프에 누적된 캡처 상태 초기화. */
export function resetInstallPromptState(): void {
  deferredEvent = null;
  installed = false;
  notify();
}

/* ── 판별 로직 ──────────────────────────────────────────────────── */

/** 홈 화면(또는 설치된 앱)에서 실행 중인지. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  // jsdom 등 matchMedia 미구현 환경 방어 (AssistantPanel 컨벤션)
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

/** iOS 계열인지. iPadOS 13+ 는 데스크톱 모드에서 Macintosh UA 로 위장하므로 터치 포인트로 보정. */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  return /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
}

/** iOS 수동 안내를 띄울 수 있는 환경인지 — 실제 "홈 화면에 추가"가 있는 Safari 만. */
export function canShowIosHint(): boolean {
  if (!isIos()) return false;
  const ua = navigator.userAgent;
  return !IN_APP_BROWSER.test(ua) && !NON_SAFARI_IOS.test(ua);
}

/**
 * 사용자가 최근에 거절했는지.
 * 손상된 값 / 최대 스누즈 기간을 넘는 값(시계 왜곡·조작)은 "거절 안 함"으로 graceful degrade —
 * 그렇지 않으면 배너가 영구히 봉인된다.
 */
export function isSnoozed(now = Date.now()): boolean {
  const until = safeGet(SNOOZE_KEY, z.number(), 0);
  return until > now && until <= now + SNOOZE_DAYS * DAY_MS;
}

export function snooze(now = Date.now()): void {
  safeSet(SNOOZE_KEY, now + SNOOZE_DAYS * DAY_MS);
}

export function clearSnooze(): void {
  safeRemove(SNOOZE_KEY);
}
